import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Space, Spin,
  Switch, Table, Tabs, Typography, notification,
} from 'antd';
import {
  ArrowLeftOutlined, DeleteOutlined, PlusOutlined, SaveOutlined, SendOutlined, UndoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import CheckinTypeTypeahead from '../CheckinTypes/CheckinTypeTypeahead';
import { canManageCarePrograms } from './careProgramPermissions';
import {
  COMPLETION_WHEN_OPTIONS,
  GATE_PERIOD_OPTIONS,
  emptyCompletionRule,
  emptyReportColumn,
  mergeProgramConfig,
} from './careProgramNormalize';
import {
  ProgramLiveSummary,
  ProgramNextHint,
  ProgramTabGoal,
  ProgramWizardNav,
  PROGRAM_WIZARD_STEPS,
} from './CareProgramFormControls';
import CareProgramEligibilityStep from './CareProgramEligibilityStep';
import CareProgramUtilizationPanel from './CareProgramUtilizationPanel';
import {
  describeEligibilityFull,
  resolveGenderOptions,
  resolveMemberScopeOptions,
  sanitizeEligibilityForSave,
  validateEligibility,
} from './careProgramEligibility';

const { Title, Text } = Typography;

const getServerError = (e, fallback) =>
  (e?.response?.data?.message != null && e.response.data.message !== '')
    ? e.response.data.message
    : (e?.message || fallback);

/** Props let the Plan Management workspace host this page inside its own tab; without them it is the standalone route. */
const AdminCareProgramDetail = ({ programId: programIdProp, onBack, onCreated } = {}) => {
  const routeParams = useParams();
  const programId = programIdProp ?? routeParams.programId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const isNew = programId === 'new';

  const canDesign = useMemo(
    () => canManageCarePrograms(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    ['basics', 'link', 'eligibility', 'rules', 'performance', 'publish'].includes(tabFromUrl)
      ? tabFromUrl
      : 'basics',
  );
  const [status, setStatus] = useState('draft');
  const [programCode, setProgramCode] = useState(null);

  const [basicsForm] = Form.useForm();
  const [linkForm] = Form.useForm();
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [config, setConfig] = useState({});
  const [planCategories, setPlanCategories] = useState([]);
  const [linkedTypePreview, setLinkedTypePreview] = useState(null);
  const [checkinTypeLabel, setCheckinTypeLabel] = useState(null);

  const completionRuleTypes = schema?.completion_rule_types ?? [
    { type: 'claim_status_gte', label: 'Claim status ≥ value' },
    { type: 'pa_header_evidence', label: 'PA header uploaded' },
    { type: 'checkin_exists', label: 'Check-in exists' },
  ];
  const reportSources = schema?.report_sources ?? [];

  const memberScopeOptions = useMemo(() => resolveMemberScopeOptions(schema), [schema]);
  const genderOptions = useMemo(() => resolveGenderOptions(schema), [schema]);
  const eligibilityReview = useMemo(
    () => describeEligibilityFull(config?.eligibility, { memberScopeOptions, genderOptions }),
    [config?.eligibility, memberScopeOptions, genderOptions],
  );

  const isArchived = status === 'archived';
  const isReadOnly = isArchived || !canDesign;

  const tabLabel = (key) => {
    if (key === 'performance') return 'Cross-org performance';
    return PROGRAM_WIZARD_STEPS.find((s) => s.key === key)?.title || key;
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams(key === 'basics' ? {} : { tab: key }, { replace: true });
  };

  const loadSchema = useCallback(async () => {
    setSchemaLoading(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.getAdminCareProgramSchema());
      if (parsed.ok) setSchema(parsed.data ?? parsed.body);
    } catch {
      // Non-fatal
    } finally {
      setSchemaLoading(false);
    }
  }, []);

  const loadPlanCategories = useCallback(async () => {
    try {
      const resp = await lib.getCategories().catch(() => null);
      const cats = Array.isArray(resp?.data?.data) ? resp.data.data : [];
      setPlanCategories(cats.map((c) => ({
        value: c.plans_categories_id,
        label: c.plans_categories_name || `Category ${c.plans_categories_id}`,
      })).filter((o) => o.value != null));
    } catch {
      // Non-fatal
    }
  }, []);

  const applyProgram = useCallback((data) => {
    const p = data?.program ?? data ?? {};
    setStatus(p.status ?? data?.status ?? 'draft');
    setProgramCode(p.program_code ?? data?.program_code);
    basicsForm.setFieldsValue({
      program_code: p.program_code ?? '',
      display_name: p.display_name ?? '',
      description: p.description ?? '',
      plans_categories_id: p.plans_categories_id ?? undefined,
      active_from: p.active_from ? dayjs(p.active_from) : null,
      active_to: p.active_to ? dayjs(p.active_to) : null,
    });
    const ctId = p.checkin_type_id ?? data?.checkin_type_id;
    const ctName = p.checkin_type_name ?? data?.checkin_type?.checkin_type_name;
    linkForm.setFieldsValue({ checkin_type_id: ctId ?? undefined });
    if (ctId) {
      setCheckinTypeLabel(ctName || `Encounter type #${ctId}`);
    }
    setConfig(mergeProgramConfig(schema?.defaults, p.config ?? data?.config ?? {}));
  }, [basicsForm, linkForm, schema?.defaults]);

  const loadDetail = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError(null);
    try {
      const parsed = lib.parseApiEnvelope(await lib.getAdminCareProgram(programId));
      if (!parsed.ok) {
        setError(parsed.message || 'Unable to load program');
        return;
      }
      const body = parsed.data ?? parsed.body;
      if (body?.designer) {
        setSchema((prev) => ({
          ...(prev || {}),
          ...body.designer,
          eligibility: {
            ...(prev?.eligibility || {}),
            ...(body.designer?.eligibility || {}),
          },
        }));
      }
      applyProgram(body);
    } catch (e) {
      setError(getServerError(e, 'Network error'));
    } finally {
      setLoading(false);
    }
  }, [applyProgram, isNew, programId]);

  useEffect(() => {
    loadSchema();
    loadPlanCategories();
  }, [loadSchema, loadPlanCategories]);

  useEffect(() => {
    if (!isNew && schema) loadDetail();
  }, [isNew, schema, loadDetail]);

  useEffect(() => {
    if (isNew && schema?.defaults) {
      setConfig(mergeProgramConfig(schema.defaults, {}));
    }
  }, [isNew, schema]);

  const searchCheckinTypes = useCallback(async (search) => {
    const parsed = lib.parseApiEnvelope(await lib.listAdminCheckinTypes({ search }));
    if (!parsed.ok) return [];
    const list = Array.isArray(parsed.data) ? parsed.data : [];
    return list.map((row) => {
      const ct = row.checkin_type ?? row;
      const id = ct.checkin_type_id ?? row.checkin_type_id;
      return {
        value: id,
        label: ct.checkin_type_name ?? row.checkin_type_name ?? `Type #${id}`,
      };
    }).filter((o) => o.value != null);
  }, []);

  const buildPayload = () => {
    const basics = basicsForm.getFieldsValue();
    const link = linkForm.getFieldsValue();
    const eligibility = sanitizeEligibilityForSave(config.eligibility);
    return {
      program_code: basics.program_code?.trim(),
      display_name: basics.display_name?.trim(),
      description: basics.description?.trim() || '',
      plans_categories_id: basics.plans_categories_id ?? null,
      checkin_type_id: link.checkin_type_id ?? null,
      active_from: basics.active_from ? basics.active_from.format('YYYY-MM-DD HH:mm:ss') : null,
      active_to: basics.active_to ? basics.active_to.format('YYYY-MM-DD HH:mm:ss') : null,
      config: { ...config, eligibility },
    };
  };

  const saveProgram = async () => {
    if (isReadOnly) {
      notification.warning({ message: isArchived ? 'Unarchive the program to edit' : 'You do not have permission to edit programs' });
      return null;
    }
    try {
      await basicsForm.validateFields();
    } catch {
      setActiveTab('basics');
      return null;
    }
    const eligibilityErrors = validateEligibility(config.eligibility, schema);
    if (eligibilityErrors.length > 0) {
      notification.error({
        message: 'Eligibility validation failed',
        description: eligibilityErrors.join(' · '),
        duration: 8,
      });
      setActiveTab('eligibility');
      return null;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const resp = isNew
        ? await lib.createAdminCareProgram(payload)
        : await lib.updateAdminCareProgram(programId, payload);
      const parsed = lib.parseApiEnvelope(resp);
      if (!lib.notifyApiEnvelope(parsed, {
        successFallback: isNew ? 'Draft program created' : 'Program saved',
        errorFallback: 'Save failed',
      })) {
        return null;
      }
      const data = parsed.data ?? parsed.body;
      const newId = data?.program_id ?? data?.program?.program_id ?? data?.id;
      if (isNew && newId) {
        if (onCreated) onCreated(String(newId));
        else navigate(`/plans/care-programs/${newId}`, { replace: true });
      }
      return data;
    } catch (e) {
      notification.error({ message: getServerError(e, 'Save failed') });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndStay = async (tabKey) => {
    const result = await saveProgram();
    if (result && tabKey) setActiveTab(tabKey);
  };

  const runPublish = async () => {
    if (isNew) {
      notification.warning({ message: 'Save the program first' });
      return;
    }
    setPublishing(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.publishAdminCareProgram(programId));
      if (!parsed.ok) {
        const errors = parsed.data?.errors ?? parsed.body?.data?.errors;
        notification.error({
          message: parsed.message || 'Publish failed',
          description: Array.isArray(errors) ? errors.join(' · ') : undefined,
          duration: 8,
        });
        return;
      }
      notification.success({ message: parsed.message || 'Program published' });
      setStatus('published');
      loadDetail();
    } catch (e) {
      notification.error({ message: getServerError(e, 'Publish failed') });
    } finally {
      setPublishing(false);
    }
  };

  const runArchive = async () => {
    if (isNew) return;
    Modal.confirm({
      title: 'Archive this program?',
      content: 'This disables all org subscriptions and removes the program from HR navigation. You can unarchive later to edit and re-publish.',
      okText: 'Archive',
      okButtonProps: { danger: true },
      onOk: async () => {
        setPublishing(true);
        try {
          const parsed = lib.parseApiEnvelope(await lib.archiveAdminCareProgram(programId));
          lib.notifyApiEnvelope(parsed, { successFallback: 'Program archived', errorFallback: 'Archive failed' });
          if (parsed.ok) {
            setStatus('archived');
            loadDetail();
          }
        } catch (e) {
          notification.error({ message: getServerError(e, 'Archive failed') });
        } finally {
          setPublishing(false);
        }
      },
    });
  };

  const runUnarchive = async () => {
    if (isNew) return;
    Modal.confirm({
      title: 'Unarchive this program?',
      content: 'Status returns to draft so you can edit and publish again. Re-enable the program on each organization separately.',
      okText: 'Unarchive',
      onOk: async () => {
        setPublishing(true);
        try {
          const parsed = lib.parseApiEnvelope(await lib.unarchiveAdminCareProgram(programId));
          lib.notifyApiEnvelope(parsed, { successFallback: 'Program unarchived to draft', errorFallback: 'Unarchive failed' });
          if (parsed.ok) {
            setStatus('draft');
            loadDetail();
          }
        } catch (e) {
          notification.error({ message: getServerError(e, 'Unarchive failed') });
        } finally {
          setPublishing(false);
        }
      },
    });
  };

  const loadLinkedTypePreview = async () => {
    const ctId = linkForm.getFieldValue('checkin_type_id');
    if (!ctId) {
      notification.warning({ message: 'Select an encounter type first' });
      return;
    }
    try {
      const parsed = lib.parseApiEnvelope(await lib.getAdminCheckinType(ctId));
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Preview failed' });
        return;
      }
      setLinkedTypePreview(parsed.data ?? parsed.body);
    } catch (e) {
      notification.error({ message: getServerError(e, 'Preview failed') });
    }
  };

  const completionRules = config?.utilization?.completion?.rules ?? [];
  const reportColumns = config?.reporting?.columns ?? [];

  const updateCompletionRuleAt = (index, patch) => {
    setConfig((prev) => {
      const rules = [...(prev.utilization?.completion?.rules ?? [])];
      if (index < 0 || index >= rules.length) return prev;
      const next = { ...rules[index], ...patch };
      delete next.key;
      delete next._rowIndex;
      rules[index] = next;
      return {
        ...prev,
        utilization: {
          ...prev.utilization,
          completion: { ...prev.utilization?.completion, rules },
        },
      };
    });
  };

  const removeCompletionRuleAt = (index) => {
    setConfig((prev) => ({
      ...prev,
      utilization: {
        ...prev.utilization,
        completion: {
          ...prev.utilization?.completion,
          rules: (prev.utilization?.completion?.rules ?? []).filter((_, i) => i !== index),
        },
      },
    }));
  };

  const updateReportColumnAt = (index, patch) => {
    setConfig((prev) => {
      const columns = [...(prev.reporting?.columns ?? [])];
      if (index < 0 || index >= columns.length) return prev;
      const next = { ...columns[index], ...patch };
      delete next._rowIndex;
      columns[index] = next;
      return {
        ...prev,
        reporting: { ...prev.reporting, columns },
      };
    });
  };

  const removeReportColumnAt = (index) => {
    setConfig((prev) => ({
      ...prev,
      reporting: {
        ...prev.reporting,
        columns: (prev.reporting?.columns ?? []).filter((_, i) => i !== index),
      },
    }));
  };

  const completionRuleColumns = useMemo(() => [
    {
      title: 'Rule type',
      dataIndex: 'type',
      render: (v, row) => (
        <Select
          style={{ width: '100%' }}
          value={v}
          disabled={isReadOnly}
          options={completionRuleTypes.map((t) => ({
            value: t.type ?? t.value,
            label: t.label ?? t.type ?? t.value,
          }))}
          onChange={(val) => updateCompletionRuleAt(row._rowIndex, { type: val })}
        />
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      width: 120,
      render: (v, row) => (
        <InputNumber
          style={{ width: '100%' }}
          value={v}
          disabled={isReadOnly || row.type !== 'claim_status_gte'}
          placeholder={row.type === 'claim_status_gte' ? 'e.g. 3' : '—'}
          onChange={(val) => updateCompletionRuleAt(row._rowIndex, { value: val })}
        />
      ),
    },
    {
      title: '',
      width: 48,
      render: (_, row) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={isReadOnly}
          onClick={() => removeCompletionRuleAt(row._rowIndex)}
        />
      ),
    },
  ], [completionRuleTypes, isReadOnly]);

  const reportColumnDefs = useMemo(() => [
    {
      title: 'Column key',
      dataIndex: 'key',
      render: (v, row) => (
        <Input
          value={v}
          disabled={isReadOnly}
          placeholder="e.g. client_name"
          onChange={(e) => updateReportColumnAt(row._rowIndex, { key: e.target.value })}
        />
      ),
    },
    {
      title: 'Header label',
      dataIndex: 'label',
      render: (v, row) => (
        <Input
          value={v}
          disabled={isReadOnly}
          placeholder="HR table header"
          onChange={(e) => updateReportColumnAt(row._rowIndex, { label: e.target.value })}
        />
      ),
    },
    {
      title: 'Data source',
      dataIndex: 'source',
      render: (v, row) => (
        <Select
          allowClear
          showSearch
          style={{ width: '100%' }}
          value={v || undefined}
          disabled={isReadOnly}
          placeholder="Pick source"
          options={reportSources.map((s) => ({
            value: s.source ?? s.value ?? s,
            label: s.label ?? s.source ?? s,
          }))}
          onChange={(val) => updateReportColumnAt(row._rowIndex, { source: val ?? '' })}
        />
      ),
    },
    {
      title: '',
      width: 48,
      render: (_, row) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={isReadOnly}
          onClick={() => removeReportColumnAt(row._rowIndex)}
        />
      ),
    },
  ], [reportSources, isReadOnly]);

  // Table row identity only — do not persist synthetic keys into config (rules have no domain key;
  // report columns use domain `key`, so Ant rowKey must not overwrite it).
  const rulesTableRows = completionRules.map((r, i) => ({ ...r, _rowIndex: i }));
  const columnsTableRows = reportColumns.map((c, i) => ({ ...c, _rowIndex: i }));

  return (
    <div style={{ padding: '16px 24px' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => (onBack ? onBack() : navigate('/plans?tab=care-programs'))}>
            Back
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {isNew ? 'New care program' : (basicsForm.getFieldValue('display_name') || programCode || `Program #${programId}`)}
          </Title>
          {!isNew && (
            <Text type="secondary">Status: <Text strong>{status}</Text></Text>
          )}
        </Space>

        {error && <Alert type="error" message={error} showIcon />}
        {isArchived && (
          <Alert
            type="warning"
            showIcon
            message="Program archived"
            description="Edits are blocked until you unarchive. Org enablement was turned off for all organizations."
          />
        )}
        {!canDesign && !isNew && (
          <Alert type="info" showIcon message="View only — you need care_programs.manage to edit programs." />
        )}
        <ProgramWizardNav activeKey={activeTab} onChange={handleTabChange} isNew={isNew} />

        <Spin spinning={loading}>
          <Tabs activeKey={activeTab} onChange={handleTabChange} renderTabBar={() => null} items={[
            {
              key: 'basics',
              label: tabLabel('basics'),
              children: (
                <Card>
                  <ProgramTabGoal tabKey="basics" />
                  <Form form={basicsForm} layout="vertical">
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="program_code"
                          label="Program code"
                          rules={[{ required: true, message: 'Required — e.g. annual_medicals' }]}
                          extra="Lowercase identifier used in URLs and org toggles."
                        >
                          <Input placeholder="annual_medicals" disabled={!isNew && status !== 'draft'} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="display_name"
                          label="Display name"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Input placeholder="Annual Medicals" />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item name="description" label="Description">
                          <Input.TextArea rows={2} placeholder="Employer annual medical examinations" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="plans_categories_id"
                          label="Limit pool (must match linked encounter type)"
                          extra="Publish fails if this differs from the encounter type's benefit pool."
                        >
                          <Select allowClear showSearch optionFilterProp="label" options={planCategories} placeholder="e.g. Annual Medicals benefit" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={6}>
                        <Form.Item name="active_from" label="Global season from">
                          <DatePicker showTime style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={6}>
                        <Form.Item name="active_to" label="Global season until">
                          <DatePicker showTime style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                  <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => saveAndStay('link')} disabled={isReadOnly}>
                    {isNew ? 'Create draft' : 'Save basics'}
                  </Button>
                  <ProgramNextHint nextTabKey="link" />
                </Card>
              ),
            },
            {
              key: 'link',
              label: tabLabel('link'),
              children: (
                <Card>
                  <ProgramTabGoal tabKey="link" />
                  <ProgramLiveSummary>
                    Clinical workflow (facilities, tariffs, PA evidence) comes from the linked encounter type.
                  </ProgramLiveSummary>
                  <Form form={linkForm} layout="vertical">
                    <Form.Item
                      name="checkin_type_id"
                      label="Encounter type"
                      rules={[{ required: !isNew, message: 'Required before publish' }]}
                    >
                      <CheckinTypeTypeahead
                        allowClear
                        minLength={1}
                        style={{ width: '100%' }}
                        placeholder="Search encounter types…"
                        fetchOptions={searchCheckinTypes}
                        options={linkForm.getFieldValue('checkin_type_id') ? [{
                          value: linkForm.getFieldValue('checkin_type_id'),
                          label: checkinTypeLabel || `Type #${linkForm.getFieldValue('checkin_type_id')}`,
                        }] : []}
                        onChange={(val, opt) => {
                          linkForm.setFieldValue('checkin_type_id', val);
                          setCheckinTypeLabel(opt?.label ?? null);
                        }}
                      />
                    </Form.Item>
                  </Form>
                  <Space>
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => saveAndStay('eligibility')} disabled={isNew}>
                      Save link
                    </Button>
                    <Button onClick={loadLinkedTypePreview} disabled={isNew}>Preview type policy</Button>
                  </Space>
                  {linkedTypePreview && (
                    <Alert
                      style={{ marginTop: 16 }}
                      type="info"
                      message={`Linked: ${linkedTypePreview?.checkin_type?.checkin_type_name ?? 'Encounter type'}`}
                      description={`Facilities: ${linkedTypePreview?.facility_mode ?? 'open'} · Tariffs: ${linkedTypePreview?.tariff_mode ?? 'open'}`}
                    />
                  )}
                  <ProgramNextHint nextTabKey="eligibility" />
                </Card>
              ),
            },
            {
              key: 'eligibility',
              label: tabLabel('eligibility'),
              children: (
                <Card>
                  <ProgramTabGoal tabKey="eligibility" />
                  <CareProgramEligibilityStep
                    eligibility={config?.eligibility}
                    schema={schema}
                    schemaLoading={schemaLoading}
                    disabled={isReadOnly}
                    onChange={(next) => setConfig((p) => ({ ...p, eligibility: next }))}
                  />
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={() => saveAndStay('rules')}
                    disabled={isNew || isReadOnly}
                    style={{ marginTop: 16 }}
                  >
                    Save eligibility
                  </Button>
                  <ProgramNextHint nextTabKey="rules" />
                </Card>
              ),
            },
            {
              key: 'rules',
              label: tabLabel('rules'),
              children: (
                <Card>
                  <ProgramTabGoal tabKey="rules" />
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div>
                      <Text strong>Duplicate gate (check-in)</Text>
                      <Row gutter={16} style={{ marginTop: 8 }}>
                        <Col xs={24} md={8}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Max completions per member</Text>
                          <InputNumber
                            min={1}
                            style={{ width: '100%', marginTop: 4 }}
                            disabled={isReadOnly}
                            value={config?.gates?.max_completed_per_enrollee ?? 1}
                            onChange={(v) => setConfig((p) => ({ ...p, gates: { ...p.gates, max_completed_per_enrollee: v ?? 1 } }))}
                          />
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Count period</Text>
                          <Select
                            style={{ width: '100%', marginTop: 4 }}
                            disabled={isReadOnly}
                            value={config?.gates?.max_completed_period ?? 'annual'}
                            options={GATE_PERIOD_OPTIONS}
                            onChange={(v) => setConfig((p) => ({ ...p, gates: { ...p.gates, max_completed_period: v } }))}
                          />
                        </Col>
                        <Col span={24}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Message when blocked</Text>
                          <Input
                            style={{ marginTop: 4 }}
                            disabled={isReadOnly}
                            value={config?.gates?.block_message ?? ''}
                            placeholder="Annual Medicals has already been utilized for this client."
                            onChange={(e) => setConfig((p) => ({ ...p, gates: { ...p.gates, block_message: e.target.value } }))}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div>
                      <Text strong>When is a member “completed”?</Text>
                      <Select
                        style={{ width: 280, marginTop: 8, display: 'block' }}
                        disabled={isReadOnly}
                        value={config?.utilization?.completion?.completed_when ?? 'any_of'}
                        options={COMPLETION_WHEN_OPTIONS}
                        onChange={(v) => setConfig((p) => ({
                          ...p,
                          utilization: {
                            ...p.utilization,
                            completion: { ...p.utilization?.completion, completed_when: v },
                          },
                        }))}
                      />
                      <Table
                        size="small"
                        style={{ marginTop: 12 }}
                        pagination={false}
                        rowKey={(r) => `rule-${r._rowIndex}`}
                        columns={completionRuleColumns}
                        dataSource={rulesTableRows}
                        locale={{ emptyText: 'Add at least one completion rule' }}
                      />
                      <Button
                        icon={<PlusOutlined />}
                        style={{ marginTop: 8 }}
                        disabled={isReadOnly}
                        onClick={() => setConfig((p) => ({
                          ...p,
                          utilization: {
                            ...p.utilization,
                            completion: {
                              ...p.utilization?.completion,
                              rules: [...(p.utilization?.completion?.rules ?? []), emptyCompletionRule()],
                            },
                          },
                        }))}
                      >
                        Add completion rule
                      </Button>
                    </div>

                    <div>
                      <Text strong>HR status labels</Text>
                      <Row gutter={16} style={{ marginTop: 8 }}>
                        {['not_done', 'completed', 'in_progress'].map((k) => (
                          <Col xs={24} md={8} key={k}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{k}</Text>
                            <Input
                              style={{ marginTop: 4 }}
                              disabled={isReadOnly}
                              value={config?.utilization?.status_labels?.[k] ?? ''}
                              onChange={(e) => setConfig((p) => ({
                                ...p,
                                utilization: {
                                  ...p.utilization,
                                  status_labels: { ...p.utilization?.status_labels, [k]: e.target.value },
                                },
                              }))}
                            />
                          </Col>
                        ))}
                      </Row>
                    </div>

                    <div>
                      <Text strong>HR report columns</Text>
                      <Table
                        size="small"
                        style={{ marginTop: 8 }}
                        pagination={false}
                        rowKey={(r) => `col-${r._rowIndex}`}
                        columns={reportColumnDefs}
                        dataSource={columnsTableRows}
                        locale={{ emptyText: 'Add columns for the utilization table' }}
                      />
                      <Button
                        icon={<PlusOutlined />}
                        style={{ marginTop: 8 }}
                        disabled={isReadOnly}
                        onClick={() => setConfig((p) => ({
                          ...p,
                          reporting: {
                            ...p.reporting,
                            columns: [...(p.reporting?.columns ?? []), emptyReportColumn()],
                          },
                        }))}
                      >
                        Add column
                      </Button>
                    </div>

                    <div>
                      <Text strong>UI hints</Text>
                      <Row gutter={16} style={{ marginTop: 8 }}>
                        <Col xs={24} md={8}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Facility check-in label</Text>
                          <Input
                            style={{ marginTop: 4 }}
                            disabled={isReadOnly}
                            value={config?.ui?.facility?.checkin_label ?? ''}
                            onChange={(e) => setConfig((p) => ({
                              ...p,
                              ui: { ...p.ui, facility: { ...p.ui?.facility, checkin_label: e.target.value } },
                            }))}
                          />
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary" style={{ fontSize: 12 }}>HR nav order</Text>
                          <InputNumber
                            style={{ width: '100%', marginTop: 4 }}
                            disabled={isReadOnly}
                            value={config?.ui?.hr?.nav_order ?? 10}
                            onChange={(v) => setConfig((p) => ({
                              ...p,
                              ui: { ...p.ui, hr: { ...p.ui?.hr, nav_order: v ?? 10 } },
                            }))}
                          />
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary" style={{ fontSize: 12 }}>HR nav section</Text>
                          <Input
                            style={{ marginTop: 4 }}
                            disabled={isReadOnly}
                            value={config?.ui?.hr?.nav_section ?? 'benefits'}
                            onChange={(e) => setConfig((p) => ({
                              ...p,
                              ui: { ...p.ui, hr: { ...p.ui?.hr, nav_section: e.target.value } },
                            }))}
                          />
                        </Col>
                      </Row>
                    </div>

                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => saveAndStay('publish')} disabled={isNew || isReadOnly}>
                      Save rules
                    </Button>
                    <ProgramNextHint nextTabKey="publish" />
                  </Space>
                </Card>
              ),
            },
            ...(!isNew ? [{
              key: 'performance',
              label: tabLabel('performance'),
              children: (
                <Card>
                  {status === 'published' ? (
                    <CareProgramUtilizationPanel
                      programCode={programCode}
                      fixedProgram
                      embedded
                      title="Cross-organization performance"
                    />
                  ) : (
                    <Alert
                      type="info"
                      showIcon
                      message="Publish to view cross-org performance"
                      description="Utilization metrics are available after the program is published and enabled on organizations."
                    />
                  )}
                </Card>
              ),
            }] : []),
            {
              key: 'publish',
              label: tabLabel('publish'),
              children: (
                <Card>
                  <ProgramTabGoal tabKey="publish" />
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Eligibility summary"
                    description={(
                      <div>
                        <div><Text strong>HR roster starts from: </Text>{eligibilityReview.rosterBasis}</div>
                        <div><Text strong>Narrowed to: </Text>{eligibilityReview.members}</div>
                        <div><Text strong>Audience: </Text>{eligibilityReview.audience}</div>
                        <div><Text strong>Org must enable program: </Text>{eligibilityReview.orgRequired}</div>
                      </div>
                    )}
                  />
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Before publishing"
                    description="Encounter type linked · at least one HR report column · limit pool matches type (if set)."
                  />
                  <Space wrap>
                    {status === 'archived' ? (
                      <Button
                        type="primary"
                        icon={<UndoOutlined />}
                        loading={publishing}
                        onClick={runUnarchive}
                        disabled={!canDesign}
                      >
                        Unarchive to draft
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="primary"
                          icon={<SendOutlined />}
                          loading={publishing}
                          onClick={runPublish}
                          disabled={isNew || status === 'published' || isReadOnly}
                        >
                          Publish program
                        </Button>
                        {status === 'published' && (
                          <Button danger loading={publishing} onClick={runArchive} disabled={!canDesign}>
                            Archive
                          </Button>
                        )}
                        <Button icon={<SaveOutlined />} loading={saving} onClick={saveProgram} disabled={isNew || isReadOnly}>
                          Save all
                        </Button>
                      </>
                    )}
                  </Space>
                  {status === 'published' && (
                    <Alert
                      style={{ marginTop: 16 }}
                      type="success"
                      showIcon
                      message="Published — enable on each organization under Corporations → Care programs tab."
                    />
                  )}
                  {status === 'archived' && (
                    <Alert
                      style={{ marginTop: 16 }}
                      type="info"
                      showIcon
                      message="After unarchive, edit and publish again, then re-enable per organization."
                    />
                  )}
                </Card>
              ),
            },
          ]} />
        </Spin>
      </Space>
    </div>
  );
};

export default AdminCareProgramDetail;
